<template>
  <UserCommonLayout
    :main-class-name="'pt0'"
    :footer-class-name="!isModify ? 'line' : ''">
    <template v-if="hasValidParam && isInit" #content>
      <LazyProSkillAssureSettingsDetailBreadCrumb
        :assessment-info="assessmentInfo"/>
      <template v-if="isModify">
        <LazyProSkillAssureSettingsCommonHeader
          :create-step="createStep"
          :assessment-title="assessmentInfo.assessmentTitle"
        />
        <LazyProSkillAssureSettingsCommonContent
          v-model:create-step="createStep"
          :assessment-id="assessmentId"
          :default-assessment="assessmentForModify"
          :default-assessment-sets="assessment.setList"
          :modify-scope="assessment.modifyScope"
          @on-cancel="onChangeModify(false)"
          @on-update="getProSkillAssureAssessmentDetail"
        />
      </template>
      <template v-else>
        <LazyProSkillAssureSettingsDetailHeader
          :assessment="assessment"
          :assessment-info="assessmentInfo"
          :modify-scope="assessment.modifyScope"
          @on-modify="onChangeModify(true)"
          @invite-candidate="handleInviteCandidate"
        />
        <div class="tabMenuFixedTop">
          <div class="containerDash-1480">
            <ul class="tabMenu flex">
              <li
                :class="{active: getAssessmentDetailTab === ProSkillAssureDetailTabMenuType.INFORMATION}"
                @click.stop="onClickTab(ProSkillAssureDetailTabMenuType.INFORMATION)">
                <a>{{ t("skillCertify-assessment-info") }}</a>
              </li>
              <li
                :class="{active: getAssessmentDetailTab === ProSkillAssureDetailTabMenuType.CANDIDATES}"
                @click.stop="onClickTab(ProSkillAssureDetailTabMenuType.CANDIDATES)">
                <a>{{ t("skillCertify-candidates-list") }}</a>
              </li>
            </ul>
          </div>
        </div>
        <LazyProSkillAssureSettingsDetailInformation
          v-if="assessmentDetailTabMenu === ProSkillAssureDetailTabMenuType.INFORMATION"
          :key="assessment"
          :assessment="assessment"
        />
        <LazyProSkillAssureSettingsDetailCandidates
          v-else
          :key="assessment"
          :invite-trigger="inviteTrigger"
          @on-update="getProSkillAssureAssessmentDetail"
        />
      </template>
    </template>
  </UserCommonLayout>
</template>

<script setup lang="ts">
import {
  LazyProSkillAssureSettingsCommonContent,
  LazyProSkillAssureSettingsCommonHeader,
  LazyProSkillAssureSettingsDetailBreadCrumb,
  LazyProSkillAssureSettingsDetailCandidates,
  LazyProSkillAssureSettingsDetailHeader,
  LazyProSkillAssureSettingsDetailInformation,
  UserCommonLayout
} from '#components';
import {useLoadingStore} from '~/store/loading/loadingStore';
import {computed, ref} from '#imports';
import {useProSkillAssureService} from '~/composables/service/useProSkillAssureService';
import {
  ProSkillAssureAssessmentDetailModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDetailModel';
import {
  ProSkillAssureAssessmentInfoModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentInfoModel';
import {useUserStore} from '~/store/user/userStore';
import {ProSkillAssureCreateStepType} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureCreateStepType';
import {
  ProSkillAssureAssessmentCreationModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentCreationModel';
import {ProSkillAssureDetailTabMenuType} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureDetailTabMenuType';
import {useDialogStore} from '~~/application/store/dialog/dialogStore';
import DialogType from '~~/application/components/Common/Dialog/model/DialogType';
import {useI18n} from 'vue-i18n';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const loadingStore = useLoadingStore();
const dialogStore = useDialogStore();
const { guard } = usePermissionGuard();

const assessment = ref(new ProSkillAssureAssessmentDetailModel());
const assessmentInfo = ref(new ProSkillAssureAssessmentInfoModel());
const assessmentForModify = ref<ProSkillAssureAssessmentCreationModel>(new ProSkillAssureAssessmentCreationModel());

const rawAssessmentId = route.params.assessmentId as string;
const hasValidParam =
  typeof rawAssessmentId === 'string' && /^\d+$/.test(rawAssessmentId.trim());
const assessmentId = ref(
  hasValidParam ? parseInt(rawAssessmentId, 10) : 0
);
const createStep = ref(ProSkillAssureCreateStepType.ASSESSMENT_SET);

const isInit = ref(false);
const isModify = ref(false);
const assessmentDetailTabMenu = ref<ProSkillAssureDetailTabMenuType>();
const inviteTrigger = ref(0);

const getAssessmentDetailTab = computed(() => {
  return route.query.tab as ProSkillAssureDetailTabMenuType ?? ProSkillAssureDetailTabMenuType.INFORMATION;
})

const handleInviteCandidate = () => {
  inviteTrigger.value++;
  getProSkillAssureAssessmentDetail();
}

const getProSkillAssureAssessmentDetail = () => {
  Promise.all([
    useProSkillAssureService().getProSkillAssureAssessmentDetail(assessmentId.value),
    useProSkillAssureService().getProSkillAssureAssessmentInfo(assessmentId.value)
  ]).then(resolve => {
    assessment.value = resolve[0];
    assessmentInfo.value = resolve[1];
    assessment.value.assessmentTitle = resolve[1].assessmentTitle;
    initData();
    loadingStore.closeLoading();
    isInit.value = true
  }).finally(() => {
    loadingStore.closeLoading();
    isInit.value = true
  });
};

const initData = () => {
  if(assessmentId.value === 0) {
    dialogStore.open({
      type: DialogType.ProSkillAssureSamplePopup
    })
  }
  assessmentForModify.value = ProSkillAssureAssessmentCreationModel.create({
    title: assessment.value.assessmentTitle,
    type: assessment.value.type,
    description: assessment.value.description,
    eyeTrackingFlag: assessment.value.eyeTrackingFlag,
    tabProctoringFlag: assessment.value.tabProctoringFlag,
    shortcutFlag: assessment.value.shortcutFlag,
    multiMonitorFlag: assessment.value.multiMonitorFlag,
    copyPasteFlag: assessment.value.copyPasteFlag,
    screenRecordingFlag: assessment.value.screenRecordingFlag,
    useExternalMonitoringFlag: assessment.value.useExternalMonitoringFlag,
    startedAt: getTimeZoneDateString(assessment.value.startedAt, assessment.value.timeZoneId),
    endedAt: getTimeZoneDateString(assessment.value.endedAt, assessment.value.timeZoneId),
    duration: assessment.value.duration,
    timeZoneId: assessment.value.timeZoneId,
    reportLangs: assessment.value.reportLangs
  });
};

const onChangeModify = (status: boolean) => {
  window.scroll(0,0);
  isModify.value = status;
}

const onClickTab = (tab: ProSkillAssureDetailTabMenuType) => {
  router.push({ query: { tab } });
};

const handlerAssessmentMenuDoNotHavePermission = () => {
  if (!guard(
      userStore.isPermissionCreateAssessment ||
      userStore.isSuperAdminType
  )) return;
}

onMounted(() => {
  if (!hasValidParam) {
    router.back();
    return;
  }
  handlerAssessmentMenuDoNotHavePermission();
  getProSkillAssureAssessmentDetail();
});

watch(() => route.query.tab, () => {
  assessmentDetailTabMenu.value = route.query.tab as ProSkillAssureDetailTabMenuType ?? ProSkillAssureDetailTabMenuType.INFORMATION;

  if(isInit.value && assessmentId.value === 0) {
    dialogStore.open({
      type: DialogType.ProSkillAssureSamplePopup
    })
  }
}, { immediate: true });
</script>

<style>
@import "~/assets/scss/business_style.scss";
</style>